import { Menu, MenuItem } from '@mui/material';
import { DocxIcon, ExportIcon, MarkdownIcon } from '../Icons/Icons';
import React from 'react';
import { cn } from '@/lib/utils';
import { Message } from '@/type/chat';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, ImageRun } from 'docx';
import LogoData from './LogoData';
import domtoimage from 'dom-to-image';
import dynamic from 'next/dynamic';
import { useTranslation } from '@/lib/i18n';

const PdfExport = dynamic(() => import('./PdfExport'), {
  ssr: false,
  loading: () => <p className="text-white text-sm px-4">Loading...</p>,
});

export type ExportIProps = {
  message: Message;
  title: string;
  contentRef: React.RefObject<HTMLDivElement>;
  toolInfoRef?: React.RefObject<HTMLDivElement>;
};
export default function Export(props: ExportIProps) {
  const { t } = useTranslation();
  const { message, title, contentRef, toolInfoRef } = props;

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClose = () => {
    setAnchorEl(null);
  };

  const exportMarkdown = () => {
    const blob = new Blob(['# ' + title + '\n\n' + message?.content], {
      type: 'text/markdown',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = sanitizeFileName(title) + '.md';
    a.click();
    URL.revokeObjectURL(url);

    handleClose();
  };

  const exportDocx = async () => {
    const generateDocument = async () => {
      let toolInfo: Paragraph = new Paragraph({});
      if (toolInfoRef?.current && toolInfoRef?.current?.innerText) {
        const base64Image = await domtoimage.toPng(toolInfoRef?.current);
        toolInfo = new Paragraph({
          children: [
            new ImageRun({
              data: base64Image,
              transformation: {
                width: 600,
                height: 365,
              },
              type: 'png',
            }),
          ],
          spacing: {
            before: 200,
            after: 200,
          },
        });
      }
      // mainContent
      const paragraphs = (contentRef?.current?.innerText || '')?.split('\n').map((text) => {
        return new Paragraph({
          children: [new TextRun(text)],
        });
      });

      return new Document({
        sections: [
          {
            children: [
              //logo
              new Paragraph({
                children: [
                  new ImageRun({
                    data: LogoData,
                    transformation: {
                      width: 175,
                      height: 30,
                    },
                    type: 'png',
                  }),
                ],
                spacing: {
                  before: 200,
                  after: 200,
                },
              }),
              // title
              new Paragraph({
                text: title,
                heading: HeadingLevel.HEADING_1,
              }),
              // toolInfo
              toolInfo,
              // content
              ...paragraphs,
            ],
          },
        ],
      });
    };
    const doc = await generateDocument();
    if (!doc) return;
    const blob = await Packer.toBlob(doc);

    // download
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = sanitizeFileName(title) + '.docx';
    link.click();
    URL.revokeObjectURL(url);

    handleClose();
  };

  return (
    <div>
      <button
        className="flex flex-row items-center  h-[32px] px-[10px] gap-1.5 text-black/70 dark:text-text-tertiary text-sm rounded-[16px] hover:bg-[#E9E9E9] dark:hover:bg-[#1e1e1e] transition duration-200 hover:text-black dark:hover:text-white"
        onClick={(event) => setAnchorEl(event.currentTarget)}
      >
        <ExportIcon />
        {t('Export')}
      </button>
      <Menu
        id="export-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: {
            style: {
              backgroundColor: '#2A2C2C',
              borderRadius: '10px',
              border: '1px solid #434444',
            },
          },
        }}
      >
        <PdfExport {...props} handleClose={handleClose} />
        <MenuItem onClick={exportMarkdown}>
          <div
            className={cn(
              'flex flex-row items-center gap-3 text-xs text-white hover:text-[#3184FF]',
            )}
          >
            <MarkdownIcon /> {t('MarkDown')}
          </div>
        </MenuItem>
        <MenuItem onClick={exportDocx}>
          <div
            className={cn(
              'flex flex-row items-center gap-3 text-xs text-white hover:text-[#3184FF]',
            )}
          >
            <DocxIcon /> {t('DOCX')}
          </div>
        </MenuItem>
      </Menu>
    </div>
  );
}

export function sanitizeFileName(fileName: string, maxLength = 50) {
  // 过滤掉特殊字符，只保留字母、数字和下划线
  let sanitized = fileName.replace(/[\/:*?"<>|]/g, '');
  // 控制文件名长度，超出最大长度时截取
  if (sanitized.length > maxLength) {
    sanitized = sanitized.substring(0, maxLength);
  }

  return sanitized;
}
